import {
  deleteProfileByName,
  downloadGeodataFiles,
  get,
  getCalibrationFile,
  getCsib64,
  getFromBase64Csib,
  getFromDc,
  getFromDcFile,
  getFromJxlFile,
  GetFromDcResponse,
  listSystems,
  postProfiles,
  putProfiles,
} from './generated';
import type {
  CoordinateSystem,
  CoordinateSystemWithCsib,
  GetCalibrationFileData,
  GetCalibrationFileError,
  GetCalibrationFileResponse,
  CreateProfileResponse,
  GetCsib64Data,
  GetCsib64Error,
  GetCsib64Response,
  GetData,
  GetError,
  GetFromBase64CsibError,
  GetFromBase64CsibResponse,
  GetResponse,
  ListSystemsData,
  ListSystemsError,
  ListSystemsResponse,
  SurveyUnits,
  UpdateProfileRequest,
  UpdateProfileResponse,
} from './generated';
import { Client } from './generated/client/types.gen';
import {
  useQuery,
  type UndefinedInitialDataOptions,
  type UseQueryResult,
} from '@tanstack/solid-query';
import { returnMappedOrThrow, returnOrThrow } from '@trimble/http-utils';
import { getLogger } from '@trimble/logging';
import {
  CalibrationFileInfo,
  CoordinateSystemData,
  CoordinateSystemFromDcFileResponse,
  DownloadGeoDataFilesOptions,
} from './coordinates-api-service.types';
import { mapCoordinateSystemData } from './coordinates-api-service.mappers';

type ListSystemsQuery = NonNullable<ListSystemsData['query']>;
type ListSystemsQueryKey = [
  'coordinates-api-service',
  'list-systems',
  ListSystemsQuery | undefined,
];
type ListSystemsQueryOptions = ReturnType<
  UndefinedInitialDataOptions<
    CoordinateSystem[],
    Error,
    CoordinateSystem[],
    ListSystemsQueryKey
  >
>;

type GetByIdQueryKey = ['coordinates-api-service', 'get-by-id', string];
type GetByIdQueryOptions = ReturnType<
  UndefinedInitialDataOptions<
    CoordinateSystemWithCsib,
    Error,
    CoordinateSystemWithCsib,
    GetByIdQueryKey
  >
>;

type GetCsib64QueryKey = ['coordinates-api-service', 'get-csib64', string];
type GetCsib64QueryOptions = ReturnType<
  UndefinedInitialDataOptions<string, Error, string, GetCsib64QueryKey>
>;

// eslint-disable-next-line no-magic-numbers -- Cache coordinate system lookups for five minutes.
const COORDINATE_SYSTEMS_STALE_TIME_MS = 5 * 60 * 1000;

const HTTP_NOT_FOUND = 404;

export class CoordinatesApiService {
  constructor(private readonly client: Client) {}

  public getCoordinateDataFromDc(
    calibrationFileInfo: CalibrationFileInfo | undefined,
    initialData?: GetFromDcResponse
  ): UseQueryResult<CoordinateSystemData, Error> {
    return useQuery(() => ({
      initialData: initialData
        ? (mapCoordinateSystemData(initialData) ?? undefined)
        : undefined,
      queryKey: ['getCoordinateDataFromDc', calibrationFileInfo?.fileName],
      queryFn: async () => {
        let calibrationFileContents: string;
        try {
          calibrationFileContents = await (
            await fetch(calibrationFileInfo!.fileDownloadLink)
          ).text();
        } catch {
          throw new Error(
            `Error fetching calibration file: ${calibrationFileInfo!.fileName}.`
          );
        }

        const response = await getFromDc({
          body: calibrationFileContents,
          headers: {
            //eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'text/plain',
          },
          client: this.client,
        });

        return returnMappedOrThrow(
          response,
          'Error occured getting Coordinates data from calibration file',
          mapCoordinateSystemData
        );
      },
      refetchOnWindowFocus: false,
      enabled: !!calibrationFileInfo,
    }));
  }

  public fetchGeoDataFiles = async (
    coordinatesDataValue: CoordinateSystemFromDcFileResponse | undefined,
    options?: DownloadGeoDataFilesOptions
  ): Promise<Map<string, ArrayBuffer>> => {
    return downloadGeoDataFilesFromResponse(
      this.client,
      coordinatesDataValue,
      options
    );
  };

  public listCoordinateSystems(
    latitude: () => number | undefined,
    longitude: () => number | undefined,
    textMatch?: () => string | undefined,
    epsgCodeMatch?: () => number | undefined
  ): UseQueryResult<CoordinateSystem[], Error> {
    return useQuery(() =>
      this.listCoordinateSystemsQueryOptions(
        latitude(),
        longitude(),
        textMatch?.(),
        epsgCodeMatch?.()
      )
    );
  }

  public listCoordinateSystemsQueryOptions(
    latitude: number | undefined,
    longitude: number | undefined,
    textMatch?: string,
    epsgCodeMatch?: number
  ): ListSystemsQueryOptions {
    const query: ListSystemsQuery | undefined =
      latitude != null && longitude != null
        ? {
            proximityLatitude: latitude,
            proximityLongitude: longitude,
            ...(textMatch ? { textMatch } : {}),
            ...(epsgCodeMatch ? { epsgCodeMatch } : {}),
          }
        : undefined;

    return {
      queryKey: ['coordinates-api-service', 'list-systems', query],
      queryFn: ({ signal }) =>
        fetchCoordinateSystems(query, this.client, signal),
      enabled: query != null,
      staleTime: COORDINATE_SYSTEMS_STALE_TIME_MS,
    };
  }

  public getCoordinateSystemById(
    id: () => string | undefined
  ): UseQueryResult<CoordinateSystemWithCsib, Error> {
    return useQuery(() => this.getCoordinateSystemByIdQueryOptions(id()));
  }

  public getCoordinateSystemByIdQueryOptions(
    id: string | undefined
  ): GetByIdQueryOptions {
    return {
      queryKey: ['coordinates-api-service', 'get-by-id', id ?? ''],
      queryFn: ({ signal }) => {
        if (!id) {
          throw new Error('Missing coordinate-system id');
        }
        return fetchCoordinateSystemById(id, this.client, signal);
      },
      enabled: Boolean(id),
      staleTime: COORDINATE_SYSTEMS_STALE_TIME_MS,
    };
  }

  public getBase64Csib(
    id: () => string | undefined
  ): UseQueryResult<string, Error> {
    return useQuery(() => this.getBase64CsibQueryOptions(id()));
  }

  public getBase64CsibQueryOptions(
    id: string | undefined
  ): GetCsib64QueryOptions {
    return {
      queryKey: ['coordinates-api-service', 'get-csib64', id ?? ''],
      queryFn: ({ signal }) => {
        if (!id) {
          throw new Error('Missing coordinate-system id');
        }
        return fetchBase64Csib(id, this.client, signal);
      },
      enabled: Boolean(id),
      staleTime: COORDINATE_SYSTEMS_STALE_TIME_MS,
    };
  }

  public async getCoordinateSystemByCsibBase64(
    csib64: string,
    signal?: AbortSignal
  ): Promise<CoordinateSystemWithCsib> {
    const result = await getFromBase64Csib({
      // eslint-disable-next-line @typescript-eslint/naming-convention -- OpenAPI query parameter name
      query: { csib_64: csib64 },
      signal,
      client: this.client,
    });

    return returnOrThrow<GetFromBase64CsibResponse, GetFromBase64CsibError>(
      result,
      'Failed to fetch coordinate system by CSIB'
    );
  }

  public getConvertedCalibrationFile(
    id: string,
    surveyUnits: SurveyUnits,
    signal?: AbortSignal
  ): Promise<string> {
    return fetchConvertedCalibrationFile(id, surveyUnits, this.client, signal);
  }

  public async uploadCrsFile(
    file: File,
    signal?: AbortSignal
  ): Promise<CoordinateSystemWithCsib[]> {
    const fileNameArray = file.name.split('.');
    const fileExtension = fileNameArray[fileNameArray.length - 1].toLowerCase();
    const uploadFn = fileExtension === 'jxl' ? getFromJxlFile : getFromDcFile;

    const result = await uploadFn({
      body: { file },
      signal,
      client: this.client,
    });

    if (result.data == null) {
      throw new Error('Failed to upload CRS file');
    }

    return Array.isArray(result.data) ? result.data : [result.data];
  }

  public async saveProjectProfile(
    tenantTrn: string,
    profileName: string,
    csid: string,
    displayName?: string | null,
    signal?: AbortSignal
  ): Promise<UpdateProfileResponse | CreateProfileResponse> {
    const body = {
      csid,
      ...(displayName !== undefined ? { displayName } : {}),
    } satisfies UpdateProfileRequest;

    const putResult = await putProfiles({
      path: { tenantTrn, profileName },
      body,
      signal,
      client: this.client,
    });

    if (putResult.data != null) {
      return putResult.data;
    }

    if (putResult.response?.status === HTTP_NOT_FOUND) {
      const postResult = await postProfiles({
        path: { tenantTrn },
        body,
        signal,
        client: this.client,
      });

      if (postResult.data == null) {
        throw new Error('Failed to create project profile.');
      }

      return postResult.data;
    }

    throw new Error('Failed to update project profile.');
  }

  public async deleteProjectProfile(
    tenantTrn: string,
    profileName: string,
    signal?: AbortSignal
  ): Promise<void> {
    const result = await deleteProfileByName({
      path: { tenantTrn, profileName },
      signal,
      client: this.client,
    });

    const status = result.response?.status;
    if (result.error == null || status === HTTP_NOT_FOUND) {
      return;
    }

    throw new Error('Failed to delete project profile.');
  }
}

const fetchCoordinateSystems = async (
  query: ListSystemsQuery | undefined,
  client: Client,
  signal?: AbortSignal
): Promise<CoordinateSystem[]> => {
  const result = await listSystems({
    query,
    signal,
    client,
  });

  return returnOrThrow<ListSystemsResponse, ListSystemsError>(
    result,
    'Failed to fetch coordinate systems'
  );
};

const fetchCoordinateSystemById = async (
  id: string,
  client: Client,
  signal?: AbortSignal
): Promise<CoordinateSystemWithCsib> => {
  const result = await get({
    query: { id } satisfies NonNullable<GetData['query']>,
    signal,
    client,
  });

  return returnOrThrow<GetResponse, GetError>(
    result,
    'Failed to fetch coordinate system by ID'
  );
};

const fetchBase64Csib = async (
  id: string,
  client: Client,
  signal?: AbortSignal
): Promise<string> => {
  const result = await getCsib64({
    query: { id } satisfies NonNullable<GetCsib64Data['query']>,
    signal,
    client,
    parseAs: 'text',
  });

  return returnOrThrow<GetCsib64Response, GetCsib64Error>(
    result,
    'Failed to fetch base64 CSIB'
  );
};

const fetchConvertedCalibrationFile = async (
  id: string,
  surveyUnits: SurveyUnits,
  client: Client,
  signal?: AbortSignal
): Promise<string> => {
  const result = await getCalibrationFile({
    query: { id, surveyUnits } satisfies NonNullable<
      GetCalibrationFileData['query']
    >,
    signal,
    client,
    parseAs: 'text',
  });

  return returnOrThrow<GetCalibrationFileResponse, GetCalibrationFileError>(
    result,
    'Failed to convert calibration file'
  );
};

export async function downloadGeoDataFilesFromResponse(
  client: Client,
  coordinatesData: CoordinateSystemFromDcFileResponse | undefined,
  options?: DownloadGeoDataFilesOptions
): Promise<Map<string, ArrayBuffer>> {
  if (!coordinatesData?.coordinateSystem?.files) {
    return new Map<string, ArrayBuffer>();
  }

  const filePromises = Object.values(
    coordinatesData.coordinateSystem.files ?? {}
  )
    .filter((name) => options?.includeGeoidFiles || !name.endsWith('.ggf'))
    .map(async (fileName) => {
      try {
        const response = await downloadGeodataFiles({
          query: { filename: fileName },
          client,
          parseAs: 'blob',
        });

        if (response.error) {
          throw new Error(`Failed to fetch geodata file: ${fileName}`);
        }

        return {
          name: fileName,
          buffer: await (response.data as Blob).arrayBuffer(),
        };
      } catch (error) {
        // Geoid files are optional for horizontal geodata (tile maps / terrain).
        // A missing .ggf should only disable feature height conversion, not drop
        // shift grids that other consumers still need.
        if (fileName.endsWith('.ggf')) {
          getLogger().warn(
            `Failed to fetch geoid file ${fileName}; feature heights will remain ellipsoidal`,
            error
          );
          return undefined;
        }
        throw error;
      }
    });

  const namedBuffers = await Promise.all(filePromises);
  const geoDataFileMap = new Map<string, ArrayBuffer>();
  for (const namedBuffer of namedBuffers) {
    if (namedBuffer) {
      geoDataFileMap.set(namedBuffer.name, namedBuffer.buffer);
    }
  }
  return geoDataFileMap;
}

export async function fetchGeoDataFilesByCsib(
  client: Client,
  csib64: string
): Promise<Map<string, ArrayBuffer>> {
  const response = await getFromBase64Csib({
    //eslint-disable-next-line @typescript-eslint/naming-convention
    query: { csib_64: csib64 },
    client,
  });

  if (response.error) {
    throw new Error(
      `Failed to fetch coordinate system from CSIB: ${JSON.stringify(response.error)}`
    );
  }

  const coordinatesData = response.data as
    | CoordinateSystemFromDcFileResponse
    | undefined;

  return downloadGeoDataFilesFromResponse(client, coordinatesData);
}
